<template>
  <dashboard-layout title="Settings" :kicker="kickerLabel" :items="navItems">
    <div v-if="loadError" class="alert alert-warning py-2 px-3 small mb-3" role="alert">
      {{ loadError }}
    </div>

    <div v-if="loading" class="text-center py-5 inter-regular-family text-muted">
      Loading settings…
    </div>

    <template v-if="!loading">
      <div class="bg-white rounded-3 border shadow-sm p-3 p-md-4 mb-4">
        <h3 class="pp-sec-title filson-black-family">Profile photo</h3>
        <p class="inter-regular-family text-muted text-full-11 mb-3">
          This photo is shown on your account. JPG, PNG or WebP, up to 3&nbsp;MB.
        </p>
        <div class="d-flex flex-column flex-sm-row align-items-start gap-3">
          <div class="settings-avatar-wrap">
            <img
              v-if="avatarSrc"
              :key="avatarSrc"
              :src="avatarSrc"
              alt=""
              class="settings-avatar-img"
              width="96"
              height="96"
              @error="onAvatarError"
            >
            <div v-else class="settings-avatar-placeholder inter-regular-family">
              {{ initials }}
            </div>
          </div>
          <div class="flex-grow-1">
            <input
              ref="fileInput"
              type="file"
              class="d-none"
              accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
              @change="onFileSelected"
            >
            <button
              type="button"
              class="pp-secondary-btn inter-bold-family text-uppercase text-full-11"
              :disabled="avatarUploading"
              @click="$refs.fileInput.click()"
            >
              {{ avatarUploading ? 'Uploading…' : 'Choose photo' }}
            </button>
            <div v-if="avatarHint" class="text-muted small mt-2">{{ avatarHint }}</div>
            <div v-if="avatarSrc" class="small mt-2">
              <a :href="avatarSrc" target="_blank" rel="noopener noreferrer" class="text-decoration-none">Open uploaded photo</a>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-3 border shadow-sm p-3 p-md-4">
        <h3 class="pp-sec-title filson-black-family">Change password</h3>
        <p class="inter-regular-family text-muted text-full-11 mb-3">
          Enter your current password, then choose a new one (at least 6 characters).
        </p>
        <form @submit.prevent="submitPassword">
          <div class="row g-2">
            <div class="col-12 col-md-6">
              <label class="pp-label" for="settings-current-password">Current password</label>
              <input
                id="settings-current-password"
                v-model="pwd.current_password"
                type="password"
                class="pp-input"
                autocomplete="current-password"
                required
              >
            </div>
            <div class="col-12 col-md-6">
              <label class="pp-label" for="settings-new-password">New password</label>
              <input
                id="settings-new-password"
                v-model="pwd.password"
                type="password"
                class="pp-input"
                autocomplete="new-password"
                required
                minlength="6"
              >
            </div>
            <div class="col-12 col-md-6">
              <label class="pp-label" for="settings-confirm-password">Confirm new password</label>
              <input
                id="settings-confirm-password"
                v-model="pwd.password_confirmation"
                type="password"
                class="pp-input"
                autocomplete="new-password"
                required
                minlength="6"
              >
            </div>
          </div>
          <button type="submit" class="pp-save inter-bold-family text-uppercase text-full-11 mt-3" :disabled="pwdSaving">
            {{ pwdSaving ? 'Saving…' : 'Update password' }}
          </button>
        </form>
      </div>
    </template>
  </dashboard-layout>
</template>

<script>
import DashboardLayout from './DashboardLayout.vue';
import { SALON_DASH_NAV, GROOMER_DASH_NAV } from '@/utils/dashboardNav';
import { readPartnerAuth, clearPartnerSession } from '@/utils/partnerProfileStorage';
import { resolveBackendAssetUrl } from '@/utils/utils';

export default {
  name: 'PartnerDashboardSettings',
  components: { DashboardLayout },
  props: {
    partnerRole: {
      type: String,
      required: true,
      validator: (v) => v === 'salon' || v === 'groomer',
    },
  },
  data() {
    return {
      loading: true,
      loadError: '',
      displayName: '',
      avatarUrl: '',
      avatarBust: 0,
      avatarUploading: false,
      avatarHint: '',
      pwdSaving: false,
      pwd: {
        current_password: '',
        password: '',
        password_confirmation: '',
      },
    };
  },
  computed: {
    kickerLabel() {
      return this.partnerRole === 'salon' ? 'Salon dashboard' : 'Groomer dashboard';
    },
    navItems() {
      return this.partnerRole === 'salon' ? SALON_DASH_NAV : GROOMER_DASH_NAV;
    },
    avatarSrc() {
      const resolved = resolveBackendAssetUrl(this.avatarUrl);
      if (!resolved) return '';
      const sep = resolved.includes('?') ? '&' : '?';
      return `${resolved}${sep}v=${this.avatarBust}`;
    },
    initials() {
      const n = String(this.displayName || '').trim();
      if (!n) return '?';
      const parts = n.split(/\s+/).filter(Boolean);
      const a = parts[0]?.[0] || '';
      const b = parts.length > 1 ? parts[parts.length - 1][0] : '';
      return (a + b).toUpperCase() || '?';
    },
  },
  async mounted() {
    const auth = readPartnerAuth();
    if (!auth?.joinType || !auth.email || !auth.token) {
      this.$router.replace({ path: '/login' });
      return;
    }
    const jt = String(auth.joinType).toLowerCase();
    if (jt !== this.partnerRole) {
      this.$router.replace({ path: `/dashboard/${jt}` });
      return;
    }
    await this.load();
  },
  methods: {
    displayNameFromProfile(p) {
      if (!p || typeof p !== 'object') return '';
      const fn = String(p.firstName || '').trim();
      const ln = String(p.lastName || '').trim();
      return [fn, ln].filter(Boolean).join(' ') || String(p.email || '').trim();
    },
    async load() {
      this.loading = true;
      this.loadError = '';
      try {
        const res = await this.$axios.get('/partner/profile');
        if (res?.data?.success) {
          this.avatarUrl = res.data.avatar_url ? String(res.data.avatar_url) : '';
          this.displayName = this.displayNameFromProfile(res.data.profile);
          this.avatarBust += 1;
        }
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) {
          clearPartnerSession();
          this.$router.replace({ path: '/login' });
          return;
        }
        this.loadError = e?.response?.data?.message || 'Could not load settings.';
      } finally {
        this.loading = false;
      }
    },
    toastApiError(e, fallback) {
      const status = e?.response?.status;
      if (status === 401) {
        clearPartnerSession();
        this.$router.replace({ path: '/login' });
        return;
      }
      const errs = e?.response?.data?.errors;
      const msg = e?.response?.data?.message || fallback;
      if (errs && typeof errs === 'object') {
        const firstKey = Object.keys(errs)[0];
        const first = Array.isArray(errs[firstKey]) ? errs[firstKey][0] : '';
        this.$toast?.error(first || msg);
      } else {
        this.$toast?.error(msg);
      }
    },
    onFileSelected(ev) {
      const file = ev?.target?.files?.[0];
      if (!file) return;
      this.avatarHint = '';
      this.uploadAvatar(file);
      ev.target.value = '';
    },
    async uploadAvatar(file) {
      this.avatarUploading = true;
      this.avatarHint = '';
      try {
        const fd = new FormData();
        fd.append('avatar', file);
        const res = await this.$axios.post('/partner/avatar', fd);
        if (res?.data?.success && res.data.avatar_url) {
          this.avatarUrl = String(res.data.avatar_url);
          this.avatarBust += 1;
          this.$toast?.success(res.data.message || 'Photo updated.');
        }
      } catch (e) {
        this.toastApiError(e, 'Could not upload photo.');
      } finally {
        this.avatarUploading = false;
      }
    },
    onAvatarError() {
      this.avatarHint = 'Could not load the uploaded photo. Click “Open uploaded photo” to verify the URL.';
    },
    async submitPassword() {
      if (this.pwd.password !== this.pwd.password_confirmation) {
        this.$toast?.error('New password and confirmation do not match.');
        return;
      }
      if (String(this.pwd.password).length < 6) {
        this.$toast?.error('New password must be at least 6 characters.');
        return;
      }
      this.pwdSaving = true;
      try {
        const res = await this.$axios.post('/partner/change-password', { ...this.pwd });
        if (res?.data?.success) {
          this.pwd = { current_password: '', password: '', password_confirmation: '' };
          this.$toast?.success(res.data.message || 'Password updated.');
        }
      } catch (e) {
        this.toastApiError(e, 'Could not update password.');
      } finally {
        this.pwdSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.pp-sec-title {
  font-size: 15px;
  margin-top: 0;
  margin-bottom: 0.75rem;
}
.pp-label {
  font-size: 11px;
  display: block;
  margin-bottom: 4px;
  font-family: interregular, sans-serif;
  color: #333;
}
.pp-input {
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
}
.pp-save {
  border: none;
  border-radius: 10px;
  padding: 12px 28px;
  background: #f8721f;
  color: #fff;
}
.pp-save:disabled {
  opacity: 0.65;
}
.pp-secondary-btn {
  border: 1px solid rgba(0, 0, 0, 0.18);
  border-radius: 10px;
  padding: 10px 18px;
  background: #fff;
  color: #333;
  font-size: 11px;
}
.pp-secondary-btn:hover:not(:disabled) {
  border-color: #f8721f;
  color: #c55a14;
}
.pp-secondary-btn:disabled {
  opacity: 0.65;
}
.settings-avatar-wrap {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
  background: #f0f1f5;
}
.settings-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.settings-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  font-size: 28px;
  font-weight: 700;
  color: #666;
}
</style>
